import { forwardRef, useEffect, useRef, useState } from 'react';
import { Avatar } from '@mui/material';
import type { AvatarProps } from '@mui/material';
import { avatarBarIndex, initials } from '../../utils/avatarHash';
import { loadAvatarObjectUrl } from '../../utils/avatarImageCache';
import { FORMER_AVATAR_SX } from '../../utils/employeeStatus';

// Extends AvatarProps so injected props (a wrapping Tooltip/Badge's hover/focus
// handlers, `aria-hidden`, `data-*`) pass straight through to the DOM node —
// without this, a Tooltip whose direct child is a PersonAvatar never opens.
export interface PersonAvatarProps extends Omit<AvatarProps, 'src' | 'children'> {
  /** Full display name — drives the initials + hashed fallback colour. */
  name: string | null | undefined;
  /** Owned-avatar URL (`/api/images/:id`); falls back to initials when absent. */
  src?: string | null;
  /** Square size in px (default 28). */
  size?: number;
  /** Inactive/former person → desaturated + dimmed, still legible (AC-9). */
  inactive?: boolean;
  /** Override the auto-derived initials font size. */
  fontSize?: number | string;
  /**
   * Accessible label. When set, the avatar is exposed as `role="img"` with this
   * label (e.g. "Ada Lovelace (Former)") so a name conveyed only by the avatar
   * still reaches assistive tech.
   */
  ariaLabel?: string;
}

/**
 * The one shared person avatar (AC-24). Renders the stored image with a stable
 * initials + hashed-colour fallback (`avatarHash`, the established
 * `bars[(hash(name)%8)+1]` rule) for people without an avatar or with an
 * unresolvable name (AC-E3 — never a crash, never a broken image).
 *
 * Auth-gated images are fetched with the bearer token and shown as a blob URL
 * (see `avatarImageCache`), lazy-loaded via `IntersectionObserver` so a long
 * list only fetches the avatars actually scrolled into view.
 */
export const PersonAvatar = forwardRef<HTMLDivElement, PersonAvatarProps>(function PersonAvatar(
  { name, src, size = 28, inactive = false, fontSize, ariaLabel, sx, ...rest },
  forwardedRef,
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const barIdx = avatarBarIndex(name);

  // Reset the resolved image whenever the source changes (or clears), so an
  // avatar swap doesn't briefly show the previous person's picture.
  useEffect(() => {
    setObjectUrl(null);
  }, [src]);

  // Defer the fetch until the avatar is near the viewport (native-lazy-ish).
  // Environments without IntersectionObserver (e.g. jsdom) load eagerly.
  useEffect(() => {
    if (!src) return;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  useEffect(() => {
    if (!src || !nearViewport) return;
    let cancelled = false;
    loadAvatarObjectUrl(src)
      .then((url) => {
        if (!cancelled) setObjectUrl(url);
      })
      .catch(() => {
        // Leave the initials fallback in place on any load failure.
        if (!cancelled) setObjectUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [src, nearViewport]);

  return (
    <Avatar
      // Injected props first (Tooltip/Badge event handlers, aria-hidden, …);
      // our explicit props below deliberately win on any conflict.
      {...rest}
      ref={(node: HTMLDivElement | null) => {
        // Keep the internal observer target and any forwarded ref (e.g. a
        // Tooltip/Badge anchor) pointing at the same node.
        rootRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      src={objectUrl ?? undefined}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      sx={[
        (theme) => ({
          width: size,
          height: size,
          flexShrink: 0,
          fontSize: fontSize ?? Math.max(10, Math.round(size * 0.4)),
          fontWeight: 600,
          backgroundColor: theme.palette.bars[barIdx].fill,
          color: theme.palette.bars[barIdx].ink,
          // Reuse the canonical former/inactive muting so it stays in step with
          // the People/Projects surfaces (TPP-34 · AC-11).
          ...(inactive && FORMER_AVATAR_SX),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {initials(name)}
    </Avatar>
  );
});

export default PersonAvatar;
